<template>
  <div class="section point-section">
    <BaseActionHeader
      class="section-header"
      spacing="none"
      title-tag="div"
    >
      <template #title>
        <div class="section-header-left">
          <h3 class="section-title">Точки заказа</h3>
          <base-toggle-arrow-button
            v-if="orderPoints && orderPoints.length > 0"
            :section-visible="sections.points"
            @click="toggleSection('points')"
          />
        </div>
      </template>
      <template v-if="sections.points" #default>
        <base-primary-button
          icon="addSquare"
          class="section-btn"
          @click="isAddPointsModalVisible = true"
        >
          Создать точку
        </base-primary-button>
      </template>
    </BaseActionHeader>

    <transition name="fade-slide">
      <div class="section-center" v-if="sections.points">
        <base-loader v-if="dataIsLoading" />

        <div
          class="points-wrapper"
          v-else-if="
            !dataIsLoading && orderPoints && orderPoints.length > 0
          "
        >
          <draggable
            v-model="localOrderPoints"
            item-key="id"
            tag="div"
            class="points-list"
            @end="onDragEnd"
            handle=".drag-handle"
            :component-data="{
              name: 'fade-slide',
              tag: 'div',
              class: 'points-list',
            }"
          >
            <template #item="{ element: point }">
              <RoutePointCard
                :point="point"
                :planned-date-text="
                  getPlannedDateText(
                    point.planned_datetime,
                    point.timezone
                  )
                "
                @edit="openEditPoint"
              >
                <template #drag-handle>
                  <!-- Иконка перетаскивания -->
                    <div
                      class="drag-handle"
                      v-if="localOrderPoints.length > 1"
                    >
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <path
                          d="M7 7H17"
                          stroke="#666"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                        <path
                          d="M7 12H17"
                          stroke="#666"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                        <path
                          d="M7 17H17"
                          stroke="#666"
                          stroke-width="2"
                          stroke-linecap="round"
                        />
                      </svg>
                    </div>
                </template>
              </RoutePointCard>
            </template>
          </draggable>
        </div>

        <div v-else class="no-data">
          <div class="no-data-content">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ccc"
              stroke-width="2"
            >
              <path
                d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
              />
              <circle cx="12" cy="9" r="2.5" />
            </svg>
            <span>Нет точек заказа</span>
          </div>
        </div>
      </div>
    </transition>

    <!-- Модальные окна -->
    <add-order-points-modal
      v-if="isAddPointsModalVisible"
      :order-id="id"
      :company-id="companyId"
      :existing-points-count="orderPoints.length"
      @close="isAddPointsModalVisible = false"
      @points-created="createSuccess"
    />
    <edit-order-points-modal
      v-if="isEditPointsModalVisible"
      :order-id="id"
      :order-point-id="getOrderPointId"
      :company-id="companyId"
      @close="isEditPointsModalVisible = false"
      @points-updated="updateSuccess"
      @points-deleted="deleteSuccess"
    />
  </div>
</template>

<script>
import { useNotificationStore } from "@/stores/notificationStore";
import { useTripStore } from "@/stores/tripStore";
import { formatDateAndTime } from "@/utils/dateUtils";
import { toLocalDatetime } from "@/utils/timezoneUtils";
import { DateTime } from "luxon";

import orderPointsApi from "@/services/api/orderPointsApi";

import AddOrderPointsModal from "@/components/Order/AddOrderPointsModal";
import EditOrderPointsModal from "@/components/Order/EditOrderPointsModal.vue";
import RoutePointCard from "@/components/RoutePoint/Card.vue";

import draggable from "vuedraggable";

export default {
  name: "OrderPoints",
  components: {
    AddOrderPointsModal,
    EditOrderPointsModal,
    RoutePointCard,
    draggable,
  },
  props: {
    id: {
      type: [Number, String],
      required: true,
    },
    companyId: {
      type: [Number, String],
      required: true,
    },
  },
  watch: {
    id() {
      this.getOrderPointsRequest();
    },
  },
  data() {
    return {
      dataIsLoading: true,

      orderPoints: [],
      localOrderPoints: [],

      isAddPointsModalVisible: false,
      isEditPointsModalVisible: false,

      getOrderPointId: null,

      sections: {
        points: true,
      },
      isReordering: false,
    };
  },
  computed: {
    notificationStore() {
      return useNotificationStore();
    },

    tripStore() {
      return useTripStore();
    },
  },
  methods: {
    async getOrderPointsRequest() {
      this.dataIsLoading = true;

      try {
        const response = await orderPointsApi.index(
          this.companyId,
          this.id
        );

        this.orderPoints = response.data.result;
        this.localOrderPoints = [...response.data.result];
      } catch (error) {
        console.error("Ошибка при загрузке точек заказа:", error);
      } finally {
        this.dataIsLoading = false;
      }
    },

    async onDragEnd() {
      if (this.isReordering) return;

      this.isReordering = true;

      try {
        const reorderData = {
          points: this.localOrderPoints.map((point, index) => ({
            id: point.id,
            sequence_number: index + 1,
          })),
        };

        await orderPointsApi.reorder(
          this.companyId,
          this.id,
          reorderData
        );

        await this.getOrderPointsRequest();

        this.notificationStore.showSuccess(
          "Порядок точек успешно изменен"
        );
      } catch (error) {
        console.error("Ошибка при изменении порядка точек:", error);

        await this.getOrderPointsRequest();
      } finally {
        this.isReordering = false;
      }
    },

    toggleSection(section) {
      this.sections[section] = !this.sections[section];
    },

    getPlannedDateText(date, timezone) {
      if (!date) return "—";
      if (!timezone) return formatDateAndTime(date);

      const localStr = toLocalDatetime(date, timezone);
      if (!localStr) return formatDateAndTime(date);

      const dt = DateTime.fromISO(localStr);
      if (!dt.isValid) return formatDateAndTime(date);

      return dt.toFormat("dd.MM.yyyy, HH:mm");
    },

    openEditPoint(pointId) {
      this.getOrderPointId = pointId;
      this.isEditPointsModalVisible = true;
    },

    async createSuccess() {
      await this.getOrderPointsRequest();

      this.notificationStore.showSuccess("Точка заказа создана");
    },

    async updateSuccess() {
      await this.getOrderPointsRequest();

      this.notificationStore.showSuccess("Точка заказа обновлена");
    },

    async deleteSuccess() {
      await this.getOrderPointsRequest();

      this.notificationStore.showSuccess("Точка заказа удалена");
    },
  },
  async created() {
    await this.getOrderPointsRequest();
  },
};
</script>

<style scoped>
.section {
  margin: 20px 0;
}

.section-header {
  margin-bottom: 15px;
}

.section-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.points-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-in-indent);
}

.drag-handle {
  cursor: grab;
  color: #999;
  display: flex;
  align-items: center;
  margin-right: 5px;
}

.drag-handle:active {
  cursor: grabbing;
}

.no-data {
  padding: 30px;
  background: #f9f9f9;
  border-radius: 10px;
  display: flex;
  justify-content: center;
}

.no-data-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #999;
}

.sortable-ghost {
  opacity: 0.5;
  background: #eef2ff;
  border: 1px dashed #667eea;
}

.sortable-drag {
  cursor: grabbing;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  transform: rotate(1deg);
}

</style>
